import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  useModal,
  useQueryGetClassroomCourseCalendar,
} from "@examspark/common-app";
import { Button, Toolbar } from "@examspark/common-ui";
import {
  IconInformationCircleLine,
  IconNewWindowLine,
} from "@examspark/common-ui/dist/components/icons";
import { Classroom } from "@examspark/types";

import { SidebarCourseCalendarLabel } from "../../calendar/SidebarCalendarLabel";
import LargeCalendarIcon from "../../common/icons/LargeCalendarIcon";
import SidebarBox, { EmptySidebarBox } from "../../common/SidebarBox";
import PlannerDayViewModal from "../../dashboard/PlannerDayViewModal";
import DeletedCalendarInfoModal from "../DeletedCalendarInfoModal";
import {
  ClassroomCalendarMonth,
  StyledCalendar,
} from "../planners/ClassroomPlannerCalendar";

const Wrapper = styled(SidebarBox)`
  overflow: hidden;
`;

const CenteredCalendar = styled(StyledCalendar)`
  display: flex;
  justify-content: center;
`;

type ClassroomPacingProps = {
  classroom?: Classroom;
};

const DeletedCallout = styled(Toolbar)`
  background-color: #fdfddf;
  padding: 0.2rem 1rem;
  font-size: 1.1rem;
  border-bottom: 1px solid #eaeaea;
  color: #545444;
`;

const ClassroomPacing = ({ classroom }: ClassroomPacingProps) => {
  const [month, setMonth] = useState<Date>(new Date());
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const navigate = useNavigate();
  const deletedCalendarInfoModal = useModal(false);

  const courseCalendarQuery = useQueryGetClassroomCourseCalendar(classroom);

  return (
    <>
      <DeletedCalendarInfoModal {...deletedCalendarInfoModal.modalProps} />
      <Wrapper
        title="Calendar"
        toolbar={
          <>
            {classroom && (
              <Button
                title="View Classroom Calendar"
                variant="outlined"
                onClick={() => navigate(`/classroom/${classroom.id}/calendar`)}
              >
                <IconNewWindowLine width="16" />
              </Button>
            )}
          </>
        }
      >
        {!!courseCalendarQuery.data && !!classroom && (
          <SidebarCourseCalendarLabel calendar={courseCalendarQuery.data} />
        )}
        {courseCalendarQuery.data?.isDeleted && (
          <DeletedCallout>
            <div style={{ flex: 1 }}>
              This calendar has been deleted or archived.
            </div>
            <div>
              <Button
                title="Info"
                icon={<IconInformationCircleLine />}
                variant="plain"
                onClick={() => deletedCalendarInfoModal.setIsOpen(true)}
              />
            </div>
          </DeletedCallout>
        )}
        {!classroom?.courseId && (
          <EmptySidebarBox>
            <LargeCalendarIcon />
            <div>Select a course to view pacing recommendations.</div>
          </EmptySidebarBox>
        )}
        {!!courseCalendarQuery.data &&
          courseCalendarQuery.data.calendar &&
          courseCalendarQuery.data.units && (
            <>
              <PlannerDayViewModal
                isOpen={isDayModalOpen}
                onRequestClose={() => setIsDayModalOpen(false)}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />
              <CenteredCalendar>
                <ClassroomCalendarMonth
                  month={month}
                  courseCalendar={courseCalendarQuery.data}
                  onMonthChange={(month) => setMonth(month)}
                  onClickDay={(value) => {
                    setSelectedDate(value);
                    setIsDayModalOpen(true);
                  }}
                />
              </CenteredCalendar>
            </>
          )}
      </Wrapper>
    </>
  );
};

export default ClassroomPacing;
